import * as Dialog from '@radix-ui/react-dialog';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import themeGet from '@styled-system/theme-get';
import { motion } from 'motion/react';
import React from 'react';
import styled from 'styled-components';
import { overflowX, overflowY, zIndex } from 'styled-system';
import { CloseButton } from '../CloseButton/CloseButton';
import { Grid } from '../Grid/Grid';
import { Text } from '../Text/Text';
import { ThemeProvider } from '../ThemeProvider/ThemeProvider';
import { useScrollWithShadow } from '../useScrollWithShadows/useScrollWithShadow';
import { Box } from '../Box/Box';
/** @public */
export const dialogSizes = ['sm', 'md', 'lg', 'xl', 'full'];
const sizeStyles = {
  sm: {
    width: 'calc(min(400px, 100vw) - 2 * 16px)'
  },
  md: {
    width: 'calc(min(640px, 100vw) - 2 * 16px)'
  },
  lg: {
    width: 'calc(min(960px, 100vw) - 2 * 16px)'
  },
  xl: {
    width: 'calc(min(1280px, 100vw) - 2 * 16px)',
    height: 'calc(100% - 2 * 48px)'
  },
  full: {
    width: '100%',
    height: '100%'
  }
};
const scrimStyles = {
  dark: 'rgba(0, 24, 51, 0.75)',
  medium: 'rgba(0, 24, 51, 0.5)',
  light: 'rgba(0, 24, 51, 0.25)'
};
const enterTransition = {
  duration: 0.25,
  ease: 'easeOut'
};
const exitTransition = {
  duration: 0.15,
  ease: 'easeIn'
};
const animationStyles = {
  default: {
    initial: {
      opacity: 0,
      scale: 0.9,
      translateY: 64
    },
    animate: {
      opacity: 1,
      scale: 1,
      translateY: 0,
      transition: enterTransition
    },
    exit: {
      opacity: 0,
      scale: 0.9,
      translateY: 32,
      transition: exitTransition
    }
  },
  sheet: {
    initial: {
      opacity: 0,
      translateY: '40%'
    },
    animate: {
      opacity: 1,
      translateY: 0,
      transition: enterTransition
    },
    exit: {
      opacity: 0,
      translateY: '60%',
      transition: exitTransition
    }
  },
  overlay: {
    initial: {
      opacity: 0,
      overflow: 'hidden'
    },
    animate: {
      opacity: 1,
      transitionEnd: {
        overflow: 'auto'
      },
      transition: enterTransition
    },
    exit: {
      opacity: 0,
      overflow: 'hidden',
      transition: exitTransition
    }
  }
};
const FloatingCloseButton = /*#__PURE__*/styled(CloseButton).withConfig({
  displayName: "Dialogstyled__FloatingCloseButton",
  componentId: "sc-1seg3h7-0"
})(["position:absolute;top:", "px;right:", "px;z-index:", ";"], props => props.dialogSize === 'full' || props.fullWidth ? 8 : -12, props => props.dialogSize === 'full' || props.fullWidth ? 8 : -12, props => themeGet('zIndices.absolute')(props));
const DialogOverlayWrapper = /*#__PURE__*/styled(motion.div).withConfig({
  displayName: "Dialogstyled__DialogOverlayWrapper",
  componentId: "sc-1seg3h7-1"
})(["", " position:fixed;inset:0;display:grid;overflow-y:auto;place-items:", ";background-color:", ";"], zIndex, props => props.sheet ? 'end center' : 'center', props => Object.keys(scrimStyles).includes(props.scrimColor) ? scrimStyles[props.scrimColor] : props.scrimColor);
const DialogContentWrapper = /*#__PURE__*/styled(motion.div).withConfig({
  displayName: "Dialogstyled__DialogContentWrapper",
  componentId: "sc-1seg3h7-2"
})(["position:relative;cursor:initial;background-color:", ";width:", ";height:", ";margin:", ";border-radius:", ";box-shadow:", ";", ""], props => themeGet('palette.background.lightest')(props), props => props.fullWidth ? '100%' : sizeStyles[props.size]?.width, props => props.sheet ? `calc(${sizeStyles[props.size]?.height} - 24px)` : sizeStyles[props.size]?.height, props => props.sheet ? `${themeGet('space.3')(props)} 0 0 0` : props.size === 'full' ? '0' : `${themeGet('space.4')(props)}
         ${themeGet('space.3')(props)}
         ${themeGet('space.5')(props)}
         ${themeGet('space.3')(props)}`, props => props.sheet ? `${themeGet(`borderRadii.${props.borderRadius}`)(props)} ${themeGet(`borderRadii.${props.borderRadius}`)(props)} 0 0` : props.size === 'full' ? 'none' : themeGet(`borderRadii.${props.borderRadius}`)(props), props => themeGet('shadows.overlay-lg')(props), props => props.hasFooterContent && `
    overflow: ${props.sheet ? 'scroll' : 'visible'};
  `);
const DialogInnerContentWrapper = /*#__PURE__*/styled.div.withConfig({
  displayName: "Dialogstyled__DialogInnerContentWrapper",
  componentId: "sc-1seg3h7-3"
})(["position:relative;display:grid;grid-template-columns:1fr;grid-template-rows:auto 1fr auto;", " ", " ", " border-radius:", ";border-top:", ";"], props => props.hasFooterContent && `height: 100%;`, overflowX, overflowY, props => props.sheet ? `${themeGet(`borderRadii.${props.borderRadius}`)(props)} ${themeGet(`borderRadii.${props.borderRadius}`)(props)} 0 0` : props.size === 'full' ? 'none' : themeGet(`borderRadii.${props.borderRadius}`)(props), props => props.hugColor && `4px solid ${themeGet('palette.' + props.hugColor)(props)}`);
export const DialogOverlay = ({
  scrimColor,
  sheet,
  children,
  zIndex
}) => {
  return React.createElement(Dialog.Portal, {
    forceMount: true
  }, React.createElement(ThemeProvider, null, React.createElement(Dialog.Overlay, {
    asChild: true,
    forceMount: true
  }, React.createElement(DialogOverlayWrapper, {
    scrimColor: scrimColor,
    sheet: sheet,
    zIndex: zIndex,
    ...animationStyles.overlay
  }, children))));
};
const SmoothTransitionBox = /*#__PURE__*/styled(Box).withConfig({
  displayName: "Dialogstyled__SmoothTransitionBox",
  componentId: "sc-1seg3h7-4"
})(["overflow:", ";transition:all 0.3s ease-in-out;"], props => props.innerContentOverflow ?? 'auto');
export const DialogContent = ({
  ariaDescription,
  ariaTitle,
  borderRadius,
  children,
  footerContent,
  fullWidth,
  headerColorScheme = 'heading',
  headerContent,
  headerContentProps,
  headerIcon,
  headerShowCloseButton,
  hugColor,
  innerContentOverflow,
  overflowX,
  overflowY,
  scrimDismiss,
  sheet,
  showCloseButton,
  showScrollShadow,
  size,
  zIndex,
  onOpenChange,
  shouldDisableTabIndex
}) => {
  const headerSizeArray = [headerIcon ? 'heading5' : 'heading4',
  // xs
  headerIcon ? 'heading5' : 'heading4',
  // sm
  headerIcon ? 'heading5' : 'heading4',
  // md
  headerIcon ? 'heading5' : 'heading4',
  // lg
  headerIcon ? 'heading4' : 'heading3',
  // xl
  headerIcon ? 'heading4' : 'heading3' // xxl
  ];
  const {
    boxShadow,
    onScrollHandler
  } = useScrollWithShadow();
  return React.createElement(Dialog.Content, {
    "aria-describedby": ariaDescription,
    onPointerDownOutside: e => {
      e.preventDefault();
      if (scrimDismiss) onOpenChange(false);
    },
    onOpenAutoFocus: e => e.preventDefault(),
    asChild: true,
    forceMount: true
  }, React.createElement(DialogContentWrapper, {
    size: size,
    sheet: sheet,
    fullWidth: fullWidth,
    borderRadius: borderRadius,
    zIndex: zIndex,
    hasFooterContent: footerContent,
    ...(sheet ? animationStyles.sheet : animationStyles.default),
    ...(typeof shouldDisableTabIndex === 'boolean' && shouldDisableTabIndex && {
      tabIndex: undefined
    })
  }, showCloseButton && React.createElement(Dialog.Close, {
    asChild: true
  }, React.createElement(FloatingCloseButton, {
    dialogSize: size,
    fullWidth: fullWidth,
    variant: 'filled'
  })), React.createElement(VisuallyHidden, {
    asChild: true
  }, React.createElement(Dialog.Title, null, ariaTitle)), React.createElement(DialogInnerContentWrapper, {
    overflowX: overflowX,
    overflowY: overflowY,
    sheet: sheet,
    hasFooterContent: footerContent,
    hugColor: hugColor,
    size: size,
    borderRadius: borderRadius
  }, headerContent && React.createElement(Grid, {
    px: [3, 3, 3, 3, '24px', '24px'],
    py: 3,
    autoFlow: 'column',
    templateColumns: 'auto 1fr',
    alignItems: 'center',
    colorScheme: headerColorScheme,
    ...headerContentProps
  }, React.createElement(Grid, {
    autoFlow: 'column',
    gap: '2'
  }, headerIcon, React.createElement(Text, {
    textStyle: size === 'sm' ? headerIcon ? 'heading5' : 'heading4' : headerSizeArray
  }, headerContent)), React.createElement(Grid, {
    justifySelf: 'end'
  }, headerShowCloseButton && React.createElement(CloseButton, {
    onClick: () => onOpenChange(false)
  }))), showScrollShadow ? React.createElement(SmoothTransitionBox, {
    innerContentOverflow: innerContentOverflow,
    style: {
      boxShadow
    },
    height: '100%',
    onScroll: onScrollHandler
  }, React.Children.map(children, child => React.cloneElement(child, {
    onScroll: onScrollHandler
  }))) : React.createElement(Box, {
    height: '100%',
    style: innerContentOverflow ? {
      overflowY: innerContentOverflow
    } : undefined
  }, children), footerContent && React.createElement(Box, {
    boxShadow: showScrollShadow ? 'lg' : 'none',
    color: 'background.lightest'
  }, React.createElement(Grid, {
    px: [3, 3, 3, 3, '24px', '24px'],
    py: 3,
    autoFlow: 'column',
    templateColumns: '1fr',
    alignItems: 'center'
  }, footerContent)))));
};